<template>
  <Teleport to="body">
    <Transition name="popup">
      <div v-if="visible && popupAd" class="popup-overlay" @click.self="handleClose">
        <div class="popup-sheet">
          <!-- 핸들 바 -->
          <div class="handle-bar">
            <div class="handle"></div>
          </div>

          <!-- 광고 콘텐츠 -->
          <div class="popup-content" @click="handleClick">
            <!-- 이미지 -->
            <div class="popup-image">
              <img :src="popupAd.imageUrl" :alt="popupAd.title" />
              <!-- AD 배지 -->
              <div class="ad-badge">AD</div>
            </div>

            <!-- 텍스트 영역 -->
            <div class="popup-text">
              <p class="popup-badge">{{ popupAd.badge || '특별 혜택' }}</p>
              <h3 class="popup-title" v-html="formatTitle(popupAd.title)"></h3>
              <p v-if="popupAd.subtitle" class="popup-subtitle">{{ popupAd.subtitle }}</p>

              <!-- CTA 버튼 -->
              <div class="popup-cta">
                <span>{{ popupAd.ctaText || '자세히 보기' }}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </div>
            </div>
          </div>

          <!-- 하단 버튼 영역 -->
          <div class="popup-actions">
            <button class="hide-today-btn" @click="handleHideToday">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
              오늘 하루 보지 않기
            </button>
            <button class="close-btn" @click="handleClose">
              닫기
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { usePopupAd } from '@/composables/main/usePopupAd'

export default {
  name: 'PopupAd',
  setup() {
    const router = useRouter()
    const { getActivePopupAd, hideForToday, incrementImpressions, incrementClicks } = usePopupAd()

    const visible = ref(false)
    const popupAd = ref(null)

    const formatTitle = (text) => {
      if (!text) return ''
      return text.replace(/\n/g, '<br/>')
    }

    const handleClick = async () => {
      if (!popupAd.value) return

      // 클릭 수 증가
      await incrementClicks(popupAd.value.id)

      // 링크 이동
      if (popupAd.value.linkUrl) {
        if (popupAd.value.linkUrl.startsWith('http')) {
          window.open(popupAd.value.linkUrl, '_blank')
        } else {
          // URL에 이미 쿼리 파라미터가 있으면 &, 없으면 ? 사용
          const separator = popupAd.value.linkUrl.includes('?') ? '&' : '?'
          router.push(`${popupAd.value.linkUrl}${separator}from=popup`)
        }
      }

      visible.value = false
    }

    const handleHideToday = () => {
      hideForToday()
      visible.value = false
    }

    const handleClose = () => {
      visible.value = false
    }

    onMounted(async () => {
      // 약간의 딜레이 후 팝업 표시 (페이지 로드 완료 후)
      setTimeout(async () => {
        const ad = await getActivePopupAd()
        if (ad) {
          popupAd.value = ad
          visible.value = true

          // 노출 수 증가
          await incrementImpressions(ad.id)
        }
      }, 500)
    })

    return {
      visible,
      popupAd,
      formatTitle,
      handleClick,
      handleHideToday,
      handleClose
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.popup-overlay {
  @apply fixed inset-0 bg-black/50 z-50 flex items-end justify-center;
}

.popup-sheet {
  @apply w-full max-w-lg bg-white rounded-t-3xl overflow-hidden;
  max-height: 85vh;
}

.handle-bar {
  @apply flex justify-center py-3;
}

.handle {
  @apply w-12 h-1.5 bg-slate-300 rounded-full;
}

.popup-content {
  @apply cursor-pointer;
}

.popup-image {
  @apply relative w-full aspect-video overflow-hidden;
}

.popup-image img {
  @apply w-full h-full object-cover;
}

.ad-badge {
  @apply absolute top-3 left-3 px-2 py-1 text-xs font-bold bg-black/60 text-white rounded;
}

.popup-text {
  @apply p-5;
}

.popup-badge {
  @apply inline-block px-3 py-1 text-xs font-semibold bg-rose-500 text-white rounded-full mb-3;
}

.popup-title {
  @apply text-xl font-bold text-slate-800 leading-tight;
}

.popup-subtitle {
  @apply mt-2 text-sm text-slate-500;
}

.popup-cta {
  @apply mt-4 flex items-center gap-1 text-rose-500 font-semibold;
}

.popup-actions {
  @apply flex border-t border-slate-100;
}

.hide-today-btn {
  @apply flex-1 flex items-center justify-center gap-2 py-4 text-sm text-slate-500 hover:bg-slate-50 transition border-r border-slate-100;
}

.close-btn {
  @apply flex-1 py-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition;
}

/* 애니메이션 */
.popup-enter-active {
  transition: all 0.3s ease-out;
}

.popup-leave-active {
  transition: all 0.2s ease-in;
}

.popup-enter-from .popup-sheet,
.popup-leave-to .popup-sheet {
  transform: translateY(100%);
}

.popup-enter-from,
.popup-leave-to {
  opacity: 0;
}
</style>
